import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyQuery } from "@apollo/react-hooks";
import { Box, Button, InputAdornment, TextField } from "@mui/material";
import emailIcon from "@iconify/icons-mdi/email";
import { InlineIcon } from "@iconify/react";
import Validator from "fastest-validator";
import { MyHrPublicLayoutLink } from "my-hr/components";
import { MY_HR_FORGOT_PASSWORD } from "my-hr/graphql/queries";
import { OcLoading } from "oc/components";
import { useOcClientState } from "oc/hooks";

const validator = new Validator();

interface MyHrForgotPasswordFormProps {
  onEmailSent: (sent: boolean) => void;
}

export const MyHrForgotPasswordForm: React.FC<MyHrForgotPasswordFormProps> = ({
  onEmailSent,
}) => {
  const { t, i18n } = useTranslation();

  const schema = {
    email: {
      type: "email",
      messages: {
        email: t("MY_HR_ERROR_MESSAGE_WRONG_EMAIL_FORMAT"),
        emailEmpty: t("OC_ERROR_MESSAGE_EMAIL_IS_REQUIRED"),
      },
    },
  };
  const check = validator.compile(schema);

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useOcClientState<boolean>(
    "loginEmailError",
    false,
  );
  const [emailErrorText, setEmailErrorText] = useOcClientState<string>(
    "loginEmailErrorText",
    "",
  );

  const [forgotPassword, { loading, data: forgotPasswordData, error: forgotPasswordError }] = useLazyQuery(MY_HR_FORGOT_PASSWORD, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (forgotPasswordData?.forgotPassword) {
      onEmailSent(true);
    }
  }, [forgotPasswordData, onEmailSent]);

  useEffect(() => {
    if (forgotPasswordError?.graphQLErrors) {
      forgotPasswordError.graphQLErrors.forEach((item) => {
        if (item.message === "Entity not found!") {
          setEmailErrorText(t("MY_HR_ERROR_MESSAGE_NO_USER_FOUND"));
          setEmailError(true);
        }
      });
    }
  }, [forgotPasswordError, t, setEmailErrorText, setEmailError]);

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let isValidated = check({ email });

    if (isValidated instanceof Promise) {
      isValidated = await isValidated;
    }

    if (isValidated !== true) {
      (isValidated as any[]).forEach((item) => {
        if (item.field === "email") {
          setEmailErrorText(item.message);
          setEmailError(true);
        }
      });
      return;
    }

    forgotPassword({ variables: { email, locale: i18n.language } });
  };

  useEffect(() => {
    const handleClear = () => {
      setEmail("");
      setEmailError(false);
      setEmailErrorText("");
    };

    return () => {
      handleClear();
    };
    // eslint-disable-next-line
  }, []);

  return (
    <Fragment>
      <OcLoading show={loading} />
      <form noValidate autoComplete="off" onSubmit={handleForgotPassword}>
        <Box paddingTop="32px" paddingBottom="18px">
          <TextField
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            label={t("MY_HR_EMAIL")}
            variant="outlined"
            fullWidth
            error={emailError}
            onFocus={() => {
              setEmailErrorText("");
              setEmailError(false);
            }}
            helperText={emailError && emailErrorText}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <InlineIcon height="24px" icon={emailIcon} color="#919699" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box padding="16px 0px 0px 0px">
          <Button
            id="send-email-button"
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disableElevation
          >
            {t("MY_HR_BUTTON_SEND")}
          </Button>
        </Box>
        <Box textAlign="center" paddingTop="18px">
          <MyHrPublicLayoutLink
            label={t("MY_HR_LINK_BACK_TO_LOGIN")}
            path="/"
          />
        </Box>
      </form>
    </Fragment>
  );
};

export default MyHrForgotPasswordForm;
